import { Ionicons } from '@expo/vector-icons';
import React, { useMemo, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { PlayerPickerModal } from '@/components/fantasy/PlayerPickerModal';
import { HowToPlay, HowToPlayButton, useHowToPlay } from '@/components/games/HowToPlay';
import { PlayerAvatar } from '@/components/match/PlayerRatings';
import { TeamCrest } from '@/components/matches/TeamCrest';
import { OutBadge } from '@/components/wc/OutBadge';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { ShareButton } from '@/components/ui/ShareButton';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import { useThemePalette } from '@/config/theme';
import { useFantasyPlayerPool, useFantasySeasons } from '@/features/fantasy/hooks';
import { useGoldenBoot, useSubmitGoldenBoot } from '@/features/games/predictors';
import { useWcEliminatedTeams } from '@/features/wc/eliminations';
import { useAuth } from '@/lib/auth/AuthProvider';
import type { GoldenBootScorer, PlayerWithTeam } from '@/lib/data/provider';
import { formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { errorKey } from '@/lib/utils/errors';

const PODIUM = ['#F4C84B', '#C7CDDA', '#CD8B53'];

function short(name: string, known: string | null): string {
  return known ?? name.split(' ').slice(-1)[0];
}

function ScorerRow({ s, mine, out }: { s: GoldenBootScorer; mine: boolean; out: boolean }) {
  const palette = useThemePalette();
  const { numeralOptions } = useLocale();
  const medal = s.rank <= 3 ? PODIUM[s.rank - 1] : null;
  return (
    <View
      className={`flex-row items-center gap-3 px-4 py-2.5 ${mine ? 'bg-volt/10' : ''}`}
      style={out ? { opacity: 0.55 } : undefined}
    >
      {medal ? (
        <View className="h-6 w-6 items-center justify-center rounded-full" style={{ backgroundColor: medal }}>
          <AppText weight="bold" tabular style={{ color: '#1A1408', fontSize: 11 }}>
            {formatNumber(s.rank, numeralOptions)}
          </AppText>
        </View>
      ) : (
        <AppText variant="caption" weight="bold" tabular className="w-6 text-center text-muted">
          {formatNumber(s.rank, numeralOptions)}
        </AppText>
      )}
      <PlayerAvatar uri={s.photo_url} name={short(s.name, s.known_as)} size={30} />
      <View className="flex-1 flex-row items-center gap-1.5">
        <AppText variant="caption" weight={mine ? 'bold' : 'medium'} className="text-ink" numberOfLines={1}>
          {short(s.name, s.known_as)}
        </AppText>
        {s.team_logo ? <TeamCrest uri={s.team_logo} name="" size="xs" /> : null}
        {out ? <OutBadge compact /> : null}
        {mine ? <Ionicons name="star" size={12} color={palette.voltBright} /> : null}
      </View>
      <AppText variant="caption" weight="bold" tabular className="text-volt-bright">
        {t_goals(s.goals, numeralOptions)}
      </AppText>
    </View>
  );
}

// tiny helper kept out of the row to avoid re-reading t() per render
function t_goals(goals: number, numeralOptions: Parameters<typeof formatNumber>[1]): string {
  return `${formatNumber(goals, numeralOptions)} ⚽`;
}

export default function GoldenBootScreen() {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const { userId } = useAuth();
  const toast = useToast();
  const howto = useHowToPlay('goldenBoot');
  const seasons = useFantasySeasons();
  const wcSeason =
    (seasons.data ?? []).find((s) => (s.name ?? '').toLowerCase().includes('world cup')) ?? seasons.data?.[0];
  const gb = useGoldenBoot(wcSeason?.id);
  const { isEliminated } = useWcEliminatedTeams();
  const pool = useFantasyPlayerPool(wcSeason?.competition_id ?? undefined);
  const submit = useSubmitGoldenBoot();
  const [pickerOpen, setPickerOpen] = useState(false);

  const myPick = gb.data?.my_pick ?? null;
  const myPickId = myPick?.player_id ?? null;
  const pickedIds = useMemo(() => (myPickId ? new Set([myPickId]) : new Set<string>()), [myPickId]);

  const onPick = (player: PlayerWithTeam) => {
    if (!wcSeason?.id) return;
    setPickerOpen(false);
    submit.mutate(
      { seasonId: wcSeason.id, playerId: player.id },
      {
        onSuccess: () => toast.show({ message: t('goldenBoot.saved'), tone: 'success' }),
        onError: (e) => toast.show({ message: t(errorKey(e)), tone: 'error' }),
      },
    );
  };

  const APP_LINK = 'https://play.google.com/store/apps/details?id=com.tashkeela.app';
  const shareMessage = `${
    myPick
      ? t('goldenBoot.shareMine', { player: short(myPick.name, myPick.known_as) })
      : t('goldenBoot.share')
  } ${APP_LINK}`;

  return (
    <Screen>
      <HowToPlay visible={howto.visible} onClose={howto.close} i18nKey="goldenBoot" icon="football" />
      <BackHeader
        title={t('goldenBoot.title')}
        right={
          <View className="flex-row items-center gap-2">
            <ShareButton message={shareMessage} accessibilityLabel={t('common.share')} />
            <HowToPlayButton onPress={howto.open} />
          </View>
        }
      />
      {gb.isPending ? (
        <View className="gap-3 px-4 pt-2">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-80 w-full" />
        </View>
      ) : gb.isError ? (
        <ErrorState error={gb.error} onRetry={() => gb.refetch()} />
      ) : (
        <>
          <ScrollView contentContainerStyle={{ paddingBottom: 36, gap: 12 }}>
            <AppText variant="caption" className="mx-4 mt-1 text-center text-soft">
              {t('goldenBoot.intro')}
            </AppText>

            {/* The caller's pick */}
            <Card className="mx-4 flex-row items-center gap-3 px-4 py-3.5">
              <View className="h-12 w-12 items-center justify-center rounded-full bg-volt-glass">
                {myPick ? (
                  <PlayerAvatar uri={myPick.photo_url} name={short(myPick.name, myPick.known_as)} size={44} />
                ) : (
                  <Ionicons name="football" size={22} color={palette.voltBright} />
                )}
              </View>
              <View className="flex-1">
                <AppText variant="micro" className="uppercase tracking-wide text-muted">
                  {t('goldenBoot.yourPick')}
                </AppText>
                {myPick ? (
                  <>
                    <View className="flex-row items-center gap-1.5">
                      <AppText variant="caption" weight="bold" className="shrink text-ink" numberOfLines={1}>
                        {short(myPick.name, myPick.known_as)}
                      </AppText>
                      {isEliminated(myPick.team_id) ? <OutBadge compact /> : null}
                    </View>
                    <AppText variant="micro" className="text-soft">
                      {myPick.rank != null
                        ? t('goldenBoot.pickRank', {
                            goals: formatNumber(myPick.goals, numeralOptions),
                            rank: formatNumber(myPick.rank, numeralOptions),
                          })
                        : t('goldenBoot.noGoalsYet')}
                    </AppText>
                  </>
                ) : (
                  <AppText variant="caption" weight="medium" className="text-soft">
                    {t('goldenBoot.noPick')}
                  </AppText>
                )}
              </View>
              {userId ? (
                <Button
                  label={myPick ? t('goldenBoot.change') : t('goldenBoot.pickCta')}
                  variant={myPick ? 'secondary' : 'primary'}
                  compact
                  loading={submit.isPending}
                  onPress={() => setPickerOpen(true)}
                />
              ) : null}
            </Card>

            {/* The live race */}
            <AppText variant="caption" weight="bold" className="mx-4 mt-1 uppercase tracking-wide text-soft">
              {t('goldenBoot.race')}
            </AppText>
            <Card className="mx-4 overflow-hidden">
              {(gb.data?.leaders ?? []).map((s, i) => (
                <View key={s.player_id} className={i > 0 ? 'border-t border-line' : ''}>
                  <ScorerRow s={s} mine={s.player_id === myPickId} out={isEliminated(s.team_id)} />
                </View>
              ))}
            </Card>
          </ScrollView>

          <PlayerPickerModal
            visible={pickerOpen}
            position={null}
            pool={pool.data ?? []}
            pickedIds={pickedIds}
            onPick={onPick}
            onClose={() => setPickerOpen(false)}
            hidePrice
          />
        </>
      )}
    </Screen>
  );
}
